import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowRight, Briefcase, Building, CheckCircle, Clock, Filter, 
  Layers, Lightbulb, Mail, MapPin, MessageSquare, RefreshCw, Shield, Sparkles, 
  Star, Target, TrendingUp, Users, Zap, ChevronDown, ChevronUp, Menu, X
} from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import TrendCard from './components/TrendCard';
import LeadForm from './components/LeadForm';
import SearchBar from './components/SearchBar';
import SearchResults from './components/SearchResults';
import { COLORS } from './utils/colors';
import { getTrendUrl } from './utils/slugs';
import { SearchContext } from './App';

// ScrollToTop component to ensure the page scrolls to top on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Dynamic filter tabs for trends section with horizontal scrolling
const TrendFilters = ({ activeFilter, setActiveFilter, categories }) => {
  const filtersRef = useRef(null);
  
  // Always include "All Trends" filter
  const filters = [
    { id: 'all', label: 'All Trends' },
    ...categories.map(category => ({
      id: category.toLowerCase().replace(/\s+/g, '-'),
      label: category
    }))
  ];
  
  // Scroll horizontally to show active filter
  useEffect(() => {
    if (filtersRef.current && activeFilter !== 'all') {
      const activeElement = filtersRef.current.querySelector(`[data-filter="${activeFilter}"]`);
      if (activeElement) {
        const container = filtersRef.current;
        const scrollLeft = activeElement.offsetLeft - container.offsetWidth / 2 + activeElement.offsetWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeFilter]);
  
  return (
    <div ref={filtersRef} className="flex overflow-x-auto pb-2 mb-4 sm:mb-6 hide-scrollbar no-scrollbar">
      <div className="flex space-x-2 min-w-min px-1">
        {filters.map(filter => (
          <button
            key={filter.id}
            data-filter={filter.id}
            className={`px-3 sm:px-4 py-2 rounded-lg transition-all whitespace-nowrap text-sm ${
              activeFilter === filter.id 
              ? 'text-white shadow-sm' 
              : 'bg-white text-gray-700 border border-gray-200 hover:border-red-300'
            }`}
            style={{ backgroundColor: activeFilter === filter.id ? COLORS.primary : '' }}
            onClick={() => setActiveFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Mobile filter drawer component
const MobileFilterDrawer = ({ isOpen, onClose, activeFilter, setActiveFilter, categories }) => {
  // Always include "All Trends" filter
  const filters = [
    { id: 'all', label: 'All Trends' },
    ...categories.map(category => ({
      id: category.toLowerCase().replace(/\s+/g, '-'),
      label: category
    }))
  ];
  
  const handleSelect = (filterId) => {
    setActiveFilter(filterId);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 md:hidden">
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4 shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Filter Trends</h3>
          <button onClick={onClose} className="p-1 rounded-full bg-gray-100">
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>
        
        <div className="space-y-2">
          {filters.map(filter => (
            <button
              key={filter.id}
              className={`flex items-center w-full px-4 py-3 rounded-lg transition-all text-left ${
                activeFilter === filter.id 
                ? 'text-white' 
                : 'bg-white text-gray-700 border border-gray-200'
              }`}
              style={{ backgroundColor: activeFilter === filter.id ? COLORS.primary : '' }}
              onClick={() => handleSelect(filter.id)}
            >
              {filter.label}
              {activeFilter === filter.id && (
                <CheckCircle className="w-5 h-5 ml-auto" />
              )}
            </button>
          ))}
        </div>
        
        <div className="pt-4 mt-4 border-t border-gray-200">
          <button 
            onClick={onClose}
            className="w-full py-3 text-white rounded-xl font-medium"
            style={{ backgroundColor: COLORS.primary }}
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
};

// HeroSection Component with improved responsiveness
const HeroSection = () => {
    const { setSearchQuery } = useContext(SearchContext) || {};
    
    return (
      <section className="pt-16 sm:pt-20 md:pt-24 lg:pt-32 pb-12 sm:pb-16 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-red-50 z-0"></div>
        
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-[1]">
          <div className="absolute top-10 right-10 w-64 h-64 bg-red-300 rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 bg-amber-300 rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-red-300 rounded-full opacity-10 blur-2xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="md:pr-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-full mb-4 sm:mb-6 animate-fadeIn">
                <Lightbulb className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-700">Innovation Intelligence Platform</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight animate-fadeInUp">
                Navigate Innovation With <span style={{color: COLORS.primary}}>Confidence</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 animate-fadeInUp animation-delay-100">
                We help forward-thinking organizations identify emerging trends, understand strategic
                implications, and transform insights into meaningful business impact.
              </p>
              
              {/* Improved search bar positioning and responsiveness */}
              <div className="mb-6 sm:mb-8 animate-fadeInUp animation-delay-200 relative z-10 max-w-xl">
                <SearchBar />
              </div>
              
              <div className="flex flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8 animate-fadeInUp animation-delay-300">
                <a 
                  href="#trends" 
                  className="px-4 sm:px-6 py-2.5 sm:py-3 text-white rounded-xl transform hover:translate-y-[-2px] shadow-sm hover:shadow font-medium text-sm sm:text-base transition-all duration-300"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  Explore Trends
                </a>
                <a 
                  href="#contact" 
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white border border-gray-300 text-gray-700 rounded-xl hover:border-red-300 hover:bg-gray-50 transition-all duration-300 transform hover:translate-y-[-2px] shadow-sm hover:shadow font-medium text-sm sm:text-base"
                >
                  Contact Us
                </a>
              </div>
              
             
            </div>
            
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl opacity-10 blur-xl" style={{ backgroundColor: COLORS.primary }}></div>
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-red-50 overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-red-100 rounded-full translate-x-20 -translate-y-20 opacity-50"></div>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">Featured Trend</h3>
                      <div className="text-xs text-gray-500">Just updated</div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-50 to-amber-50 rounded-xl p-4 mb-6 relative overflow-hidden">
                    <div className="absolute bottom-0 right-0 opacity-10">
                      <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M60 0C26.9 0 0 26.9 0 60C0 93.1 26.9 120 60 120C93.1 120 120 93.1 120 60C120 26.9 93.1 0 60 0ZM60 108C33.5 108 12 86.5 12 60C12 33.5 33.5 12 60 12C86.5 12 108 33.5 108 60C108 86.5 86.5 108 60 108Z" fill={COLORS.primary}/>
                        <path d="M65 30L65 90L85 60L65 30Z" fill={COLORS.primary}/>
                        <path d="M45 30L45 90L25 60L45 30Z" fill={COLORS.primary}/>
                      </svg>
                    </div>
                    
                    <div className="flex justify-between items-start mb-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={{backgroundColor: `${COLORS.primary}20`, color: COLORS.primary}}>
                        Design Thinking
                      </span>
                      <span className="text-sm text-gray-500">Medium-term</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Human-Centered Innovation</h3>
                    <p className="text-gray-600 text-sm mb-4">Organizations shifting from technology-driven to human-centered innovation approaches.</p>
                    
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Maturity</span>
                          <span>82%</span>
                        </div>
                        <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{width: '82%', backgroundColor: COLORS.primary}}></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Disruption Potential</span>
                          <span>High</span>
                        </div>
                        <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{width: '85%', backgroundColor: COLORS.primary}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <a 
                    href="#trends"
                    className="flex items-center justify-center gap-2 w-full py-3 text-white rounded-xl transition-colors shadow-sm hover:bg-red-700"
                    style={{ backgroundColor: COLORS.primary }}
                  >
                    <span>View Full Analysis</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile featured trend card - only shown on small screens */}
        <div className="md:hidden mt-8 mx-4">
          <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-red-50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-red-600" />
              </div>
              <h3 className="font-bold text-gray-900">Featured Trend</h3>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-amber-50 rounded-xl p-3 mb-4">
              <div className="flex justify-between items-start mb-2">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{backgroundColor: `${COLORS.primary}20`, color: COLORS.primary}}>
                  Design Thinking
                </span>
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-1">Human-Centered Innovation</h3>
              <p className="text-gray-600 text-xs mb-3">Organizations shifting to human-centered innovation approaches.</p>
              
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Maturity</span>
                <span>82%</span>
              </div>
              <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full" style={{width: '82%', backgroundColor: COLORS.primary}}></div>
              </div>
            </div>
            
            <a 
              href="#trends"
              className="flex items-center justify-center gap-2 w-full py-2 text-white rounded-xl text-sm"
              style={{ backgroundColor: COLORS.primary }}
            >
              <span>View Analysis</span>
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>
    );
  };
// Trends section for landing page
const TrendsSection = ({ trends }) => {
  const navigate = useNavigate();
  const { searchQuery } = useContext(SearchContext) || {};
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleTrends, setVisibleTrends] = useState(trends);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [showAllTrends, setShowAllTrends] = useState(false);
  
  // Extract unique categories from trends data
  const uniqueCategories = [...new Set(trends.map(trend => trend.category))];
  
  // Update visible trends when filter or trends change
  useEffect(() => {
    if (!searchQuery) {
      if (activeFilter === 'all') {
        setVisibleTrends(trends);
      } else {
        // Find the category label that matches the active filter ID
        const categoryLabel = uniqueCategories.find(
          cat => cat.toLowerCase().replace(/\s+/g, '-') === activeFilter
        );
        
        // Filter trends by the matching category
        if (categoryLabel) {
          setVisibleTrends(trends.filter(t => t.category === categoryLabel));
        }
      }
    } else {
      // If there's a search query, just use the trends passed from parent which will be search results
      setVisibleTrends(trends);
    }
  }, [activeFilter, trends, searchQuery, uniqueCategories]);
  
  // Handle trend selection by navigating to trend URL
  const handleSelectTrend = (trendId) => {
    const selectedTrend = trends.find(t => t.id === trendId);
    if (selectedTrend) {
      navigate(getTrendUrl(selectedTrend));
    }
  };
  
  // Limit initial display for better performance
  const displayLimit = 6;
  const displayedTrends = showAllTrends ? visibleTrends : visibleTrends.slice(0, displayLimit);
  const hasMoreTrends = visibleTrends.length > displayLimit;
  
  return (
    <section id="trends" className="py-12 sm:py-16 md:py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 sm:mb-8">
          <div className="mb-4 md:mb-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2 sm:mb-4" style={{backgroundColor: `${COLORS.primary}15`, color: COLORS.primary}}>
              <TrendingUp className="w-4 h-4" style={{color: COLORS.primary}} />
              <span className="text-sm font-medium" style={{color: COLORS.primary}}>
                {searchQuery ? 'Search Results' : 'Innovation Trends'}
              </span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
              {searchQuery ? 'Search Results' : 'Emerging Innovation Opportunities'}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl">
              {searchQuery 
                ? `Found ${trends.length} trends matching "${searchQuery}"`
                : 'Explore high-impact trends reshaping industries and creating new strategic possibilities.'}
            </p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {!searchQuery && (
              <>
                <button 
                  className="p-2 bg-white border border-gray-200 rounded-lg hover:border-red-300 transition-colors"
                  onClick={() => setIsFilterDrawerOpen(true)}
                >
                  <Filter className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 bg-white border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
                  <RefreshCw className="w-5 h-5 text-gray-600" />
                </button>
              </>
            )}
            <div className="hidden md:block w-48 md:w-64">
              <SearchBar hideResults={true} />
            </div>
          </div>
        </div>
        
        {/* If search is active, show SearchResults component, otherwise show filtered trends */}
        {searchQuery ? (
          <SearchResults />
        ) : (
          <>
            {/* Desktop Filters */}
            <div className="hidden sm:block">
              <TrendFilters 
                activeFilter={activeFilter} 
                setActiveFilter={setActiveFilter} 
                categories={uniqueCategories}
              />
            </div>
            
            {/* Mobile Filters - button and drawer */}
            <div className="sm:hidden mb-4">
              <button
                className="flex items-center justify-between w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-700"
                onClick={() => setIsFilterDrawerOpen(true)}
              >
                <span className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span>Filter: {activeFilter === 'all' ? 'All Trends' : uniqueCategories.find(
                    cat => cat.toLowerCase().replace(/\s+/g, '-') === activeFilter
                  )}</span>
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            
            <MobileFilterDrawer 
              isOpen={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
              categories={uniqueCategories}
            />
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-16">
              {displayedTrends.map(trend => (
                <TrendCard key={trend.id} trend={trend} onSelect={handleSelectTrend} />
              ))}
            </div>
            
            {/* Show More button if needed */}
            {hasMoreTrends && !showAllTrends && (
              <div className="text-center mb-8">
                <button
                  onClick={() => setShowAllTrends(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg hover:border-red-300 bg-white text-gray-700 transition-colors shadow-sm font-medium"
                >
                  <span>Show More Trends</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <div className="text-center">
              <div className="inline-block rounded-2xl p-6 sm:p-8 max-w-3xl bg-gradient-to-br from-red-50 to-amber-50 border border-red-100 shadow-sm">
                <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-xl bg-white shadow-sm">
                  <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Looking for Specific Innovation Insights?</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">
                  Our innovation experts track emerging trends across industries. Contact us for a custom analysis 
                  relevant to your specific business challenges and opportunities.
                </p>
                <button className="px-4 sm:px-6 py-2.5 sm:py-3 text-white rounded-xl transition-all duration-300 shadow-sm hover:shadow transform hover:translate-y-[-2px] font-medium text-sm sm:text-base"
                  style={{ backgroundColor: COLORS.primary }}>
                  Request Custom Research
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// Modern approach section with cards
const ApproachSection = () => {
  const steps = [
    {
      icon: Target,
      title: "Innovation Insights",
      description: "We continuously scan for emerging innovation approaches, technologies, and methodologies across industries."
    },
    {
      icon: TrendingUp,
      title: "Opportunity Recognition",
      description: "Our analysts identify meaningful patterns and emerging opportunities where innovation can create value."
    },
    {
      icon: Layers,
      title: "Strategy Development",
      description: "We transform insights into strategic options, helping organizations navigate complexity with confidence."
    },
    {
      icon: Target,
      title: "Implementation Support",
      description: "We provide hands-on guidance to help organizations implement effective innovation approaches."
    }
  ];
  
  // For mobile step cards, we'll show one at a time with horizontal scrolling
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const stepsRef = useRef(null);
  
  const nextStep = () => {
    setActiveStepIndex((prev) => (prev + 1) % steps.length);
  };
  
  const prevStep = () => {
    setActiveStepIndex((prev) => (prev - 1 + steps.length) % steps.length);
  };
  
  useEffect(() => {
    if (stepsRef.current) {
      const scrollAmount = activeStepIndex * (stepsRef.current.offsetWidth / steps.length);
      stepsRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [activeStepIndex, steps.length]);
  
  return (
    <section id="approach" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-red-50 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-100 rounded-full mb-2 sm:mb-4">
            <Lightbulb className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-red-700">Our Methodology</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
            How We Navigate Innovation
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            We combine rigorous research with practical frameworks to help organizations
            identify and implement effective innovation approaches.
          </p>
        </div>
        
        {/* Desktop steps grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16">
          {steps.map((step, idx) => (
            <div 
              key={idx} 
              className="bg-white rounded-xl p-5 sm:p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 transform hover:translate-y-[-4px]"
            >
              <div className="p-3 sm:p-4 rounded-xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6" style={{backgroundColor: `${COLORS.primary}15`}}>
                <step.icon className="w-6 h-6 sm:w-8 sm:h-8" style={{color: COLORS.primary}} />
              </div>
              <h3 className="font-bold text-lg sm:text-xl text-gray-900 mb-2 sm:mb-3">{step.title}</h3>
              <p className="text-gray-600 text-sm sm:text-base">{step.description}</p>
            </div>
          ))}
        </div>
        
        {/* Mobile step carousel */}
        <div className="md:hidden mb-10">
          <div className="relative">
            <div 
              ref={stepsRef}
              className="flex overflow-x-scroll snap-x snap-mandatory no-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {steps.map((step, idx) => (
                <div 
                  key={idx} 
                  className="min-w-full flex-shrink-0 snap-center px-4"
                >
                  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm h-full">
                    <div className="p-3 rounded-xl w-12 h-12 flex items-center justify-center mb-4" style={{backgroundColor: `${COLORS.primary}15`}}>
                      <step.icon className="w-6 h-6" style={{color: COLORS.primary}} />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Navigation buttons */}
            <button 
              onClick={prevStep}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 bg-white rounded-full p-2 shadow-md" 
              aria-label="Previous"
            >
              <ChevronDown className="w-5 h-5 text-gray-700 rotate-90" />
            </button>
            <button 
              onClick={nextStep}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1 bg-white rounded-full p-2 shadow-md" 
              aria-label="Next"
            >
              <ChevronDown className="w-5 h-5 text-gray-700 -rotate-90" />
            </button>
          </div>
          
          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-4">
            {steps.map((_, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveStepIndex(idx)}
                className={`w-2 h-2 rounded-full ${idx === activeStepIndex ? 'bg-red-600' : 'bg-gray-300'}`}
                aria-label={`Go to step ${idx + 1}`}
              />
            ))}
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 sm:p-8 border border-red-100 shadow-sm">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Our Innovation Framework</h3>
              <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">
                Our approach combines strategic foresight with practical implementation methodologies to
                help organizations identify and capture innovation opportunities.
              </p>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="p-1 bg-red-100 rounded-full mt-1">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm sm:text-base">Customized Innovation Analysis</p>
                    <p className="text-xs sm:text-sm text-gray-600">Tailored to your industry and specific challenges</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="p-1 bg-red-100 rounded-full mt-1">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm sm:text-base">Innovation Workshops</p>
                    <p className="text-xs sm:text-sm text-gray-600">Interactive sessions to align teams and develop strategies</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="p-1 bg-red-100 rounded-full mt-1">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm sm:text-base">Ongoing Advisory Support</p>
                    <p className="text-xs sm:text-sm text-gray-600">Continuous guidance throughout your innovation journey</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2 bg-gradient-to-br from-red-50 to-amber-50 rounded-xl p-4 sm:p-6 relative">
              <img 
                src="/inno.png" 
                alt="Innovation Framework"
                className="rounded-lg w-full"
              />
              <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-md" style={{backgroundColor: COLORS.primary}}>
                <div className="text-xs sm:text-sm font-medium">Innovation Framework</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Modern contact section
const ContactSection = () => {
  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2 sm:mb-4" style={{backgroundColor: `${COLORS.primary}15`, color: COLORS.primary}}>
              <MessageSquare className="w-4 h-4" style={{color: COLORS.primary}} />
              <span className="text-sm font-medium" style={{color: COLORS.primary}}>Get in Touch</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-6">
              Let's Navigate Innovation Together
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8">
              Our team of innovation experts can help you identify opportunities specific to your business challenges.
            </p>
            
            <div className="space-y-4 sm:space-y-5 mb-6 sm:mb-8">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="p-1.5 sm:p-2 rounded-lg mt-1" style={{backgroundColor: `${COLORS.primary}15`}}>
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" style={{color: COLORS.primary}} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Customized Innovation Analysis</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">Tailored insights relevant to your specific industry and challenges</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="p-1.5 sm:p-2 rounded-lg mt-1" style={{backgroundColor: `${COLORS.primary}15`}}>
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" style={{color: COLORS.primary}} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Innovation Workshops</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">Interactive sessions to align your teams around innovation opportunities</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="p-1.5 sm:p-2 rounded-lg mt-1" style={{backgroundColor: `${COLORS.primary}15`}}>
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" style={{color: COLORS.primary}} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Ongoing Advisory</h3>
                  <p className="text-gray-600 text-xs sm:text-sm">Continuous support from our innovation experts as your strategic partners</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>We respect your privacy and maintain strict confidentiality</span>
            </div>
            
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a href="#" className="inline-flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-xs sm:text-sm">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                <span className="text-gray-800 font-medium">Zagreb, Croatia</span>
              </a>
              <a href="mailto:hello@lusidea.com" className="inline-flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-xs sm:text-sm">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                <span className="text-gray-800 font-medium">hello@lusidea.com</span>
              </a>
            </div>
          </div>
          
          <div>
            <LeadForm />
          </div>
        </div>
      </div>
    </section>
  );
};

// Landing Page Component
const LandingPage = ({ trends }) => {
  return (
    <div className="min-h-screen bg-white">
      <ScrollToTop />
      <Header transparent={true} />
      <HeroSection />
      <TrendsSection trends={trends} />
      <ApproachSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default LandingPage;